/* global Modernizr, TurbolinksAnimate */

import $ from 'jquery';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { contain } from 'contain-cover';
import debounce from 'lodash/debounce';
import Headroom from 'headroom.js';
import hoverintent from 'hoverintent';
import minBy from 'lodash/minBy';
import MultiClamp from 'multi-clamp';
import objectFitImages from 'object-fit-images';
import Plyr from 'plyr';
import Tooltip from 'tooltip.js';
import PubSub from 'pubsub-js';
import scrollbarWidth from 'scrollbar-width';
import Swiper from 'swiper';
import throttle from 'lodash/throttle';
import trim from 'lodash/trim';
import Turbolinks from 'turbolinks';
import 'turbolinks-animate';
import upperFirst from 'lodash/upperFirst';
import zenscroll from 'zenscroll';
import whatInput from 'what-input';
import vhCheck from 'vh-check';
import Vimeo from '@vimeo/player';

import 'jquery-migrate/dist/jquery-migrate.min';

import './map/jquery.mousewheel.min';
import './map/jquery-ui.min';
import './map/jquery.iviewer';
import exampleMapData from './map/map-data.json';

import 'ap-image-zoom/lib/js/ap-image-zoom';

import { trackEvent, trackPageview } from './analytics';

import MQHelper from './mq-helper';
import parseQueryString from './parse-query-string';
import './once';
import './resizestartstop';

const mq = new MQHelper({ watch: true });

vhCheck();

Turbolinks.start();

const selectors = {
  document,
  window,

  html: 'html',
  body: 'body',

  anchorLinks: 'a[href^="#"]',

  navBar: '.js-navbar',
  navToggle: '.js-nav-toggle',
  epochListToggle: '.js-epoch-list-toggle',

  videoPlayer: '.js-player',
  audioPlayer: '.js-audio-player',

  content: '#content',

  skipHeaderButton: '.js-skip-header',

  overlay: '.js-overlay',
  overlayContent: '.js-overlay-content',
  overlayClose: '.js-overlay-close',

  audioToggle: '.js-audio-toggle',

  homeTextToggle: '.js-home-text-toggle',

  slideshow: '.js-slideshow',
  headerSlideshow: '.js-header-slideshow',

  tocSection: '[data-toc]',
  tocContainer: '.js-toc',
  tocToggle: '.js-toc-toggle',
  tocContent: '.js-toc-content',
  tocProgress: '.js-toc-progress',

  epochNavigationButton: '.js-epoch-navigation-button',

  placeOverlayTrigger: '[data-trigger-place-overlay]',
  placeOverlayClose: '[data-close-place-overlay]',
  placeOverlay: '[data-place-overlay]',

  mapContainer: '.js-map-container',
  mapLegend: '.js-map-legend',
  mapLegendToggle: '.js-map-legend-toggle',
  mapSkip: '.js-map-skip',
  europeMap: '.js-europe-map',

  videoMap: '.js-video-map',
  videoMapTrigger: '.js-video-map-trigger',
  videoMapCaptions: '.js-video-map-captions',
  videoMapCaptionsToggle: '.js-video-map-captions-toggle',
  videoMapChapterTitle: '.js-video-map-chapter-title',
  videoMapChapterPrev: '.js-video-map-chapter-prev',
  videoMapChapterNext: '.js-video-map-chapter-next',

  thread: '.js-thread',
  threadInner: '.js-thread-inner',
  threadText: '.js-thread-text',
  threadTextToggle: '.js-thread-text-toggle',

  zoomableImage: '.js-image-zoom',
  zoomableImageIn: '.js-image-zoom-in',
  zoomableImageOut: '.js-image-zoom-out',

  objectImage: '.js-object-image',
  objectMarkerContainer: '.js-object-marker-container',
  objectMarker: '.js-object-marker',
  objectMarkerTrigger: '.js-object-marker-trigger',
  objectSlideshow: '.js-object-slideshow',

  objectPopupTrigger: '.js-object-popup',
};

const config = {
  smoothScrollingSpeed: 600,
  objectFitInterval: 3000,
  transitionDuration: 0,
  prefetchInternalLinks: false,
  breakpoints: {},
};

export default class App {

  constructor(_config = {}) {

    // Merge defaults and passed config object
    this.config = {
      ...config,
      ..._config,
    };

    // Read shared config from CSS
    this.readCSSConfig();

    // On DOM loaded: init all events and sub-modules
    $(this.init.bind(this));
  }

  init() {
    // Add breakpoints to MQ helper
    mq.addQueries(this.config.breakpoints);

    // Cache elements for later use
    this.cacheElements();

    // Pub/sub and event bindings
    this.setupEvents();

    // Polyfills
    this.polyfillObjectFit();

    // Modules
    this.setupTurboLinks();
    this.setupSmoothAnchorScrolling();
    this.setupExternalLinks();
    this.markBodyDuringResize();
    this.setupNavigation();
    this.setupSkipHeaderButton();
    this.setupHomepage();
    this.setupTableOfContents();
    this.setupPlaceOverlays();
    this.setupSlideshows();
    this.setupHeaderSlideshows();
    this.setupVideoPlayers();
    this.setupAudioPlayers();
    this.setupEuropeMaps();
    this.setupVideoMaps();
    this.setupAudioToggle();
    this.setupObjectSlideshows();
    this.setupThreads();
    this.setupImageZoom();
    this.setupOverlays();
    this.setupObjectPopups();
    this.setupObjectMarkers();
    this.setupEpochNavigationButton();

    // Trigger load event to init everything now
    $(document).trigger('statechange');

    // Mark page as loaded
    this.$e.html.removeClass('preload');

    const scrollbar = scrollbarWidth() || 0;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbar}px`);
  }

  // Fetch elements for all defined selectors

  cacheElements() {
    this.selectors = selectors;
    this.elements = Object.assign({}, ...Object.keys(this.selectors).map(k => ({ [k]: $(k) })));
    this.$e = this.elements;
  }

  // Events and pub/sub for interaction with other modules

  setupEvents() {
    // Update CSS config on mediaquery change
    PubSub.subscribe('mediaquery.change', this.readCSSConfig.bind(this));

    // Custom event on mediaquery change
    PubSub.subscribe('mediaquery.change', (/* event, mq */) => {
      console.log('Breakpoint', mq.current);
    });

    // Ajax behaviour and state changes
    $(document).on('statechange', this.onStateChange.bind(this));
    $(document).on('beforecache', this.beforeDomCache.bind(this));
  }

  // Configure turbolinks

  setupTurboLinks() {
    const defaultAnimation = 'fadein';
    const defaultDuration = '.3s';

    // Enable page transitions
    TurbolinksAnimate.init({ animation: defaultAnimation, duration: '0s' });

    $(document).on('turbolinks:visit', () => {
      $('.toc')
        .removeClass('is-setup')
        .removeClass('is-open');
    });

    $(document).on('turbolinks:before-render', () => {
      this.prevPageTemplate = this.getPageTemplate();
      this.prevPageIndex = this.getPageIndex();
    });

    $(document).on('turbolinks:load', () => {
      this.pageTemplate = this.getPageTemplate();
      this.pageIndex = this.getPageIndex();

      let animation = defaultAnimation;
      let duration = defaultDuration;

      const isSameTemplate = this.pageTemplate === 'epoch' && this.prevPageTemplate === 'epoch';
      const isSameIndex = this.pageIndex === this.prevPageIndex;

      if (isSameTemplate && !isSameIndex) {
        animation = this.pageIndex < this.prevPageIndex ? 'fadeInLeft' : 'fadeInRight';
        duration = '1s';
      }

      TurbolinksAnimate.init({ animation, duration });
    });

    // Move turbolinks events into general namespace

    $(document).on('turbolinks:before-cache', () => {
      $(document).trigger('beforecache');
    });
    $(document).on('turbolinks:load', () => {
      $(document).trigger('statechange');
    });

    // Pre-fetch links on hover

    $(document).on('turbolinks:load', () => {
      this.prefetchInternalLinks();
    });
    this.prefetchInternalLinks();
  }

  // Prefetch internal links on hover

  prefetchInternalLinks() {
    if (!this.config.prefetchInternalLinks) return;

    $('a[href^="/"]:not([data-turbolinks=false])')
      .once('prefetch')
      .each((index, node) => {
        hoverintent(node, () => {
          const href = node.getAttribute('href');
          if (this.$e.prefetcher) {
            if (this.$e.prefetcher.attr('href') !== href) {
              this.$e.prefetcher.attr('href', href);
            }
          }
          else {
            this.$e.prefetcher = $('<link>').attr('rel', 'prefetch').attr('href', href).appendTo('header');
          }
        }, () => {});
      });
  }

  beforeDomCache() {
    $('.lazyloaded').removeClass('.lazyloaded');
    $('.lazyloading').removeClass('.lazyloading');
  }

  // Handler that executes after every page load (http or ajax)

  onStateChange() {
    this.scrollToCurrentHash();
  }

  // Check if page has template (body class name: template-xxx)

  getPageTemplate(element = null) {
    return $(element || 'body').attr('class').match(/\btemplate-(\S*)/)[1];
  }

  getPageIndex(element = null) {
    const matches = $(element || 'body').attr('class').match(/\bindex-(\d*)/);
    return matches ? parseInt(matches[1], 10) : 0;
  }

  pageHasTemplate(template, element = null) {
    const templates = Array.isArray(template) ? template : [template];
    const selector = templates.map(t => `.template-${t}`).join(',');
    return $(element || 'body').is(selector);
  }

  getSectionNameDefault(element) {
    const $section = $(element).closest(selectors.tocSection);
    return $section.data('toc-default-lang') || $section.data('toc') || '';
  }

  // Read and initialize all shared CSS config values

  readCSSConfig() {
    // Default transition duration in ms
    this.config.transitionDuration = this.getCSSConfigValue('transition') || 0;

    // Breakpoints / media queries
    if (Object.keys(this.config.breakpoints).length === 0) {
      const cssBreakpoints = this.getCSSConfigValue('breakpoints');
      this.config.breakpoints = parseQueryString(cssBreakpoints);
    }

    PubSub.publish('css.readconfig');
  }

  // Read shared config from CSS via <meta> tag's font-family

  getCSSConfigValue(key) {
    let configStr = $(`.js-css-config[data-key="${key}"]`).css('font-family');
    configStr = trim(configStr.trim(), '"\''); // browsers re-quote string styles
    return configStr;
  }

  // Polyfill object-fit for images, if necessary

  polyfillObjectFit() {
    if (Modernizr.objectfit) return;

    const triggerObjectFit = () => {
      objectFitImages(null, {
        watchMQ: true,
      });
    };

    triggerObjectFit();
    setTimeout(triggerObjectFit, config.objectFitInterval / 2);
    setInterval(triggerObjectFit, config.objectFitInterval);
  }

  // Add body class while resizing window

  markBodyDuringResize() {
    $(window).on('resizestart', () => {
      this.$e.html.addClass('is-resizing');
    });
    $(window).on('resizestop', () => {
      this.$e.html.removeClass('is-resizing');
    });
  }

  // Open external links in new tab

  setupExternalLinks() {
    const processLinks = () => {
      $('a')
        .once('external')
        .filter('[href^="http"], [href^="//"]')
        .not(`[href*="${window.location.host}"]`)
        .attr('rel', 'noopener noreferrer')
        .attr('target', '_blank');
    };

    $(document).on('statechange', processLinks);
    processLinks();
  }

  // Scroll to hash / anchor

  scrollTo(hash, immediate = false) {
    const anchor = hash.replace('#', '');

    let $target = $(`#${anchor}`);
    $target = $target.length ? $target : $(`#anchor__${anchor}`);
    $target = $target.length ? $target : $(`[name="${anchor}"]`);

    if ($target.length) {
      const target = $target.get(0);
      if (immediate) {
        this.getScroller().to(target, 0);
      }
      else {
        this.getScroller().to(target);
      }
      return true;
    }

    return false;
  }

  getScroller(element = null, duration = null, offset = 0) {
    const defaultDuration = duration !== null ? duration : this.config.smoothScrollingSpeed;
    const scrollElem = element || document.scrollingElement || document.body;
    return zenscroll.createScroller(scrollElem, defaultDuration, offset);
  }

  // Scroll to current hash

  scrollToCurrentHash() {
    const hash = window.location.hash.replace('#', '');

    if (hash) {
      this.scrollTo(hash, true);
    }
  }

  // Make anchor links scroll smoothly

  setupSmoothAnchorScrolling() {
    zenscroll.setup(this.config.smoothScrollingSpeed, 0);

    $(document).on('click', this.selectors.anchorLinks, (event) => {
      if (!window.noZensmooth) return;
      const $anchor = $(event.target).closest(this.selectors.anchorLinks);
      const href = $anchor.get(0).hash;
      if (this.scrollTo(href)) {
        event.preventDefault();
      }
    });
  }

  // Navigation and header

  setupNavigation() {

    // Toggle navigation on button click

    $(document).on('click', this.selectors.navToggle, (event) => {
      $('body').toggleClass('is-nav-visible');
      event.preventDefault();
    });

    // Toggle epoch list on click and hover
    // hide on scroll

    $(document).on('click', this.selectors.epochListToggle, (event) => {
      $('body').toggleClass('is-epoch-list-visible');
      event.preventDefault();
    });

    $(window).on('scroll', debounce(() => {
      $('body').removeClass('is-epoch-list-visible');
    }, 100, { leading: true, trailing: false }));

    // Hide/show navigation based on scroll behaviour

    const scrollTolerance = 4;
    const headroom = new Headroom(document.documentElement, {
      offset: scrollTolerance,
      tolerance: {
        down: scrollTolerance,
        up: 0,
      },
    });
    headroom.init();

    // Calculate nav toggle size

    const calculcateNav = () => {
      const $navToggle = $(this.selectors.navToggle);
      const navToggleWidth = Math.round($navToggle.outerWidth());
      const $navBar = $(this.selectors.navBar);
      const navBarHeight = Math.round($navBar.outerHeight(true));
      document.documentElement.style.setProperty('--nav-toggle-width', `${navToggleWidth}px`);
      document.documentElement.style.setProperty('--nav-bar-height', `${navBarHeight}px`);
    };
    $(window).on('resizestop', calculcateNav.bind(this));
    calculcateNav();
    $('html').addClass('has-nav-toggle-width');
  }

  // Calculate thread height for animation

  setupThreads() {
    const clampOptions = {
      clamp: 8,
      splitByWords: true,
      disableCssClamp: true,
    };

    const calculateThreadHeight = (thread) => {
      const height = Math.round($(selectors.threadInner, thread).outerHeight());
      thread.style.setProperty('--content-height', `${height}px`);
    };

    const calculateAllThreadHeights = () => {
      $(selectors.thread).each((index, thread) => {
        calculateThreadHeight(thread);
      });
    };

    const clampText = (text, forceToggle = false) => {
      const numLines = $(window).height() * 0.6 / 27; // 60vh / 18px * 1.5lh;
      const $text = $(text);
      const $thread = $text.closest(selectors.thread);
      let clamp = $text.data('clamp');
      const isClamped = $text.hasClass('is-clamped');

      // Disable clamping when a) force-toggling b) un-expanding
      const forceOpen = (isClamped && forceToggle);
      const onClampStart = () => !forceOpen;

      const onClampEnd = ({ didClamp }) => {
        $text.toggleClass('is-clamped', didClamp);
        $text.toggleClass('is-expanded', forceOpen);
        calculateThreadHeight($thread.get(0));
      };

      // Track custom event
      if (forceOpen) {
        trackEvent({ eventCategory: 'Emphases', eventAction: 'open', eventLabel: $thread.data('title') });
      }

      if (clamp) {
        clamp.reload({
          clamp: numLines, useOriginalText: true, onClampStart, onClampEnd,
        });
      }
      else {
        clamp = new MultiClamp(text, {
          ...clampOptions, clamp: numLines, onClampStart, onClampEnd,
        });
        $text.data('clamp', clamp);
      }
    };

    const clampAllTexts = () => {
      $(selectors.threadText).each((index, text) => {
        clampText(text);
      });
    };

    $(document).on('click', selectors.threadTextToggle, (event) => {
      const $text = $(event.target).closest(selectors.thread).find(selectors.threadText);
      if ($text.length) {
        clampText($text.get(0), true);
      }
    });

    $(document).on('statechange', clampAllTexts);
    $(window).on('resizestop', clampAllTexts);

    $(document).on('statechange', calculateAllThreadHeights);
    $(window).on('resizestop', calculateAllThreadHeights);
  }

  // Table of contents / progress indicator

  setupTableOfContents() {
    $(document).on('statechange', () => {
      const $toc = $(selectors.tocContainer).once();
      if (!$toc.length) return;

      const $sections = $(selectors.tocSection);
      if (!$sections.length) {
        $toc.addClass('is-empty');
        return;
      }

      const $content = $toc.find(selectors.tocContent);
      const $progress = $toc.find(selectors.tocProgress);

      const sections = $sections.get().map((section) => {
        const $el = $(section);
        const id = ($el.attr('id') || '').replace(/^anchor__/, '');
        const name = $el.data('toc');
        const nameDefault = $el.data('toc-default-lang');
        const type = $el.data('toc-type') || 'section';
        const action = $el.data('toc-action');
        const $item = $(`<li data-type="${type}"><span>${name}</span></li>`);
        const atBeginning = $sections.index(section) === 0;

        $item.on('click', () => {
          if (type === 'next' && action) {
            Turbolinks.visit(action);
          }
          else {
            this.getScroller().to(section);
            trackEvent({ eventCategory: 'Contents', eventAction: 'click', eventLabel: nameDefault || name });
          }
        });

        if (type === 'section') {
          $content.append($item);
        }

        return {
          type, id, name, nameDefault, $el, $item, atBeginning,
        };
      });

      const calculatePositions = () => {
        sections.forEach((section) => {
          // eslint-disable-next-line
          section.top = section.$el.offset().top;
          // eslint-disable-next-line
          section.bottom = section.top + section.$el.outerHeight() - 10;
        });
      };

      calculatePositions();
      $(window).on('resizestop', calculatePositions);
      $(document).one('beforecache', () => {
        $(window).off('resizestop', calculatePositions);
      });

      let top;
      let center;
      let doc;
      let win;
      let progress;
      let closest;
      let current;

      const handleScroll = throttle(() => {
        top = $(window).scrollTop();
        doc = $(document).height();
        win = $(window).height();
        center = top + win / 2;
        progress = top / (doc - win);
        $progress.css('transform', `scaleY(${progress})`);

        closest = minBy(sections, (s) => {
          const distanceToTop = Math.abs(s.top - center);
          const distanceToBottom = Math.abs(s.bottom - center);
          return Math.min(distanceToBottom, distanceToTop);
        });

        $toc.toggleClass('is-at-end', closest.type === 'next');
        $toc.removeClass('is-open');

        if (!current || closest.name !== current.name) {
          const $closest = closest.$item;
          $closest.addClass('is-current');
          $closest.addClass('is-highlighted');
          setTimeout(() => {
            $closest.removeClass('is-highlighted');
          }, 2000);

          const sectionUrl = closest.id && !closest.atBeginning ? `#${closest.id}` : window.location.pathname;
          window.history.replaceState(undefined, undefined, sectionUrl);

          if (current) {
            const $current = current.$item;
            $current.removeClass('is-current');
            $current.addClass('was-highlighted');
            $current.removeClass('is-highlighted');
            setTimeout(() => {
              $current.removeClass('was-highlighted');
            }, this.config.transitionDuration);
          }

          current = closest;

          trackEvent({ eventCategory: 'Contents', eventAction: 'enter', eventLabel: closest.nameDefault || closest.name });
        }
      }, 100);

      handleScroll();
      $(window).on('scroll', handleScroll);
      $(document).one('beforecache', () => {
        $(window).off('scroll', handleScroll);
      });

      setTimeout(() => {
        $toc.addClass('is-setup');
      }, 1000);
    });

    $(document).on('beforecache', () => {
      $(selectors.tocContainer).removeClass('is-empty is-setup');
      $(selectors.tocContent).html('');
    });

    $(document).on('click', selectors.tocToggle, () => {
      $(selectors.tocContainer).toggleClass('is-open');
    });
  }

  // Image zoom

  setupImageZoom() {
    $(document).on('statechange', () => {
      const $container = $(selectors.zoomableImage).once('zoom');
      if (!$container.length) return;

      const $image = $container.find('img');
      const $in = $(selectors.zoomableImageIn).once('zoom');
      const $out = $(selectors.zoomableImageOut).once('zoom');

      $image.apImageZoom({
        cssWrapperClass: 'image-zoom-container',
        autoCenter: true,
        loadingAnimation: 'custom',
        loadingAnimationData: () => {
          $container.addClass('is-loaded');
        },
        initialZoom: 'min',
        minZoom: 'contain',
        maxZoom: 1.0,
        zoomStep: 0.1,
        hammerPluginEnabled: false,
        mouseWheelPluginEnabled: false,
        disabled: false,
      });

      const zoom = (direction) => {
        const triggerEvent = `zoom${upperFirst(direction)}`;
        $image.apImageZoom(triggerEvent);
        trackEvent({ eventCategory: 'Object', eventAction: 'zoom', eventLabel: `Zoom ${direction}` });
      };

      const updateZoomStatus = () => {
        const isMax = $image.apImageZoom('isMaxZoomed');
        const isMin = $image.apImageZoom('isMinZoomed');
        const canZoom = !(isMax && isMin);
        $in.prop('disabled', $image.apImageZoom('isMaxZoomed'));
        $out.prop('disabled', $image.apImageZoom('isMinZoomed'));
        $container.add($in).add($out).toggleClass('can-zoom', canZoom);
      };

      $image.on('apizLoaded', () => {
        updateZoomStatus();
      });

      $in.add($out).on('click', (event) => {
        const direction = $(event.target).closest('button').data('action') === 'zoom-out' ? 'out' : 'in';
        zoom(direction);
        updateZoomStatus();
      });
    });

    $(document).on('beforecache', () => {
      const $image = $(selectors.zoomableImage).find('img');
      $image.apImageZoom('destroy');
    });
  }

  // Object markers / tooltips

  setupObjectMarkers() {
    $(document).on('statechange', () => {
      const $containers = $(selectors.objectMarkerContainer).once('marker');
      if (!$containers.length) return;

      $containers.each((i, node) => {
        const $container = $(node);

        // Calculate max size based on image measurements
        const $image = $container.closest(selectors.objectImage).find('img');
        const imageWidth = parseInt($image.attr('width'), 10);
        const imageHeight = parseInt($image.attr('height'), 10);
        if (!imageWidth || !imageHeight) return;

        const calculateMarkerContainer = () => {
          const containerWidth = $image.width();
          const containerHeight = $image.height();
          const {
            width,
            height,
          } = contain(containerWidth, containerHeight, imageWidth, imageHeight);
          $container.get(0).style.setProperty('--max-width', `${width}px`);
          $container.get(0).style.setProperty('--max-height', `${height}px`);
        };
        calculateMarkerContainer();
        $(window).on('resizestop', calculateMarkerContainer);
        $(document).on('beforecache', () => {
          $(window).off('resizestop', calculateMarkerContainer);
        });

        // Configure tooltips

        const $markers = $container.find(selectors.objectMarker);
        const trigger = whatInput.ask() === 'touch' || whatInput.ask('intent') === 'touch' ? 'click' : 'hover focus';
        $markers.each((index, marker) => {
          const $marker = $(marker);
          const title = $marker.data('title');

          $marker.on('mouseenter focus click', () => {
            trackEvent({ eventCategory: 'Tooltips', eventAction: 'show', eventLabel: title });
          });

          // eslint-disable-next-line
          new Tooltip($marker, {
            html: true,
            offset: '-25%p, 200%r',
            title: marker.innerHTML,
            trigger,
            closeOnClickOutside: true,
          });
        });

        $container.addClass('is-setup');
      });
    });

    // Enable/disable focus/markers

    $(document).on('click', selectors.objectMarkerTrigger, () => {
      $('body').toggleClass('are-object-markers-visible');
      const visible = $('body').hasClass('are-object-markers-visible');
      trackEvent({ eventCategory: 'Object', eventAction: 'tooltips', eventLabel: visible ? 'Enable tooltips' : 'Disable tooltips' });
    });

    $(document).on('beforecache', () => {
      $('body').removeClass('are-object-markers-visible');
    });
  }

  // Epoch navigation button

  setupEpochNavigationButton() {
    $(document).on('click', selectors.epochNavigationButton, (event) => {
      const $button = $(event.target).closest(selectors.epochNavigationButton);
      if ($button.data('clicked')) return;
      $button.data('clicked', true);

      const action = $button.data('direction') || 'click';
      const section = $button.data('section');
      trackEvent({ eventCategory: 'Epoch navigation', eventAction: action, eventLabel: section });
    });
  }

  // Overlay container

  setupOverlays() {
    $(document).on('click', selectors.overlayClose, () => {
      this.closeOverlay();
    });
    $(document).on('beforecache', () => {
      this.closeOverlay(true);
    });
  }

  openOverlay(content, url = false) {
    const $overlay = $(selectors.overlay);
    const $content = $overlay.find(selectors.overlayContent);

    if ($overlay.hasClass('is-visible')) {
      $overlay.addClass('is-changing');
      setTimeout(() => {
        $content.html(content);
        $overlay.removeClass('is-changing');
        setTimeout(() => {
          $(document).trigger('statechange');
        }, 100);
      }, this.config.transitionDuration);
    }
    else {
      $content.html(content);
      $overlay.addClass('is-visible');
      disableBodyScroll($content.get(0));
      // Save page URL to restore later
      $overlay.data('page-url', url ? window.location.href : null);
      setTimeout(() => {
        $(document).trigger('statechange');
      }, 100);
    }

    // Change URL
    if (url) {
      window.history.replaceState(undefined, undefined, url);
      trackPageview(url);
    }
  }

  closeOverlay(immediate = false) {
    const $overlay = $(selectors.overlay);
    const $content = $overlay.find(selectors.overlayContent);

    $overlay.removeClass('is-visible');
    enableBodyScroll($content.get(0));

    if (immediate) {
      $content.html('');
    }
    else {
      setTimeout(() => {
        $content.html('');
      }, this.config.transitionDuration * 2);
    }

    // Restore previous URL if overlay has set one
    const pageUrl = $overlay.data('page-url');
    if (pageUrl) {
      window.history.replaceState(undefined, undefined, pageUrl);
    }
  }

  // Object popups

  setupObjectPopups() {
    const openPopup = (url) => {
      if (!url) return;

      $('body').addClass('is-loading');
      $.get(url, (data) => {
        this.openOverlay($(data).find(selectors.content).html(), url);
      }).always(() => {
        $('body').removeClass('is-loading');
      });
    };

    $(document).on('click', selectors.objectPopupTrigger, (event) => {
      const url = $(event.target).closest(selectors.objectPopupTrigger).prop('href');
      openPopup(url);
      event.preventDefault();
    });
  }

  // Open/close place overlays on click

  setupPlaceOverlays() {
    // Open on click of thumbnail
    $(document).on('click', this.selectors.placeOverlayTrigger, (event) => {
      const overlayID = $(event.target).closest(selectors.placeOverlayTrigger).data('trigger-place-overlay');
      const $overlay = $(selectors.placeOverlay).filter(`[data-place-overlay="${overlayID}"]`);
      $overlay.addClass('is-open');
      trackEvent({ eventCategory: 'Architecture', eventAction: 'open', eventLabel: $overlay.data('title') });
    });

    // Close on click of close button
    $(document).on('click', this.selectors.placeOverlayClose, (event) => {
      const $overlay = $(event.target).closest(selectors.placeOverlay);
      $overlay.removeClass('is-open');
    });

    // Close on click of overlay
    $(document).on('click', this.selectors.placeOverlay, (event) => {
      const $overlay = $(event.target);
      if ($overlay.is(this.selectors.placeOverlay)) {
        $overlay.removeClass('is-open');
      }
    });
  }

  // Europe maps

  setupEuropeMaps() {
    $(document).on('statechange', () => {
      const $map = $(selectors.europeMap).once();
      if (!$map.length) return;

      // Standard Zoomlevel (33, 44, 66, 100, 150)
      // Mobile 33, Desktop 44
      let zoom = $(window).width() < 500 ? 33 : 44;
      const data = $map.data('layers') || exampleMapData;

      $map.iviewer({
        offset: [0, 200],
        zoom,
        data,
        onAfterZoom(event, newZoom) {
          if (zoom === newZoom) return;
          trackEvent({ eventCategory: 'Map', eventAction: 'zoom', eventLabel: newZoom > zoom ? 'Zoom in' : 'Zoom out' });
          zoom = newZoom;
        },
        onFinishLoad() {
          console.log('Map loaded');
        },
        onErrorLoad() {
          console.error('Error loading map');
        },
      });

      $map.iviewer('activateMouseWheel', false);
    });

    $(document).on('beforecache', () => {
      $(selectors.europeMap).iviewer('destroy');
    });

    $(document).on('click touchend', selectors.mapSkip, (event) => {
      const $container = $(event.target).closest(selectors.mapContainer);

      // Pause player
      const player = $container.find(selectors.videoMap).data('player');
      if (player) {
        player.pause().catch(console.error);
      }

      // Skip section
      const newScrollY = $container.offset().top + $container.outerHeight() + 1;
      this.getScroller().toY(newScrollY, 1000);

      trackEvent({ eventCategory: 'Contents', eventAction: 'skip', eventLabel: this.getSectionNameDefault($container) });
    });

    $(document).on('click', selectors.mapLegendToggle, (event) => {
      const $container = $(event.target).closest(selectors.mapContainer);
      $container.toggleClass('is-legend-open');
      event.stopPropagation();
      trackEvent({ eventCategory: 'Map', eventAction: $container.hasClass('is-legend-open') ? 'open legend' : 'close legend' });
    });

    $(document).on('click touchstart touchend scroll touchmove', selectors.mapLegend, (event) => {
      event.stopPropagation();
    });

    const calculateToggleHeight = () => {
      const $legend = $(selectors.mapLegend);
      const $map = $legend.closest(selectors.europeMap);
      if ($legend.length && $map.length) {
        const height = $(selectors.mapLegendToggle).height();
        $map.get(0).style.setProperty('--legend-toggle-height', `${height}px`);
      }
    };
    calculateToggleHeight();
    $(document).on('statechange', calculateToggleHeight);
    $(window).on('resizestop', calculateToggleHeight);
    $(document).one('beforecache', () => {
      $(window).off('resizestop', calculateToggleHeight);
    });
  }

  // Audio toggle (mute/unmute)

  setupAudioToggle() {
    this.isAudioMuted = false;

    $(document).on('click', selectors.audioToggle, () => {
      this.isAudioMuted = !this.isAudioMuted;
      this.$e.html.toggleClass('is-audio-muted', this.isAudioMuted);

      const $map = $(selectors.videoMap);
      const player = $map.data('player');

      if (player) {
        player.setMuted(this.isAudioMuted).then((muted) => {
          console.log('Muted?', muted);
        }).catch(console.error);
      }

      trackEvent({ eventCategory: 'Audio', eventAction: this.isAudioMuted ? 'mute' : 'unmute' });
    });
  }

  // Video maps

  setupVideoMaps() {
    $(document).on('statechange', () => {
      const $map = $(selectors.videoMap).once('map');
      if (!$map.length) return;

      const iframe = $map.find('iframe').get(0);
      if (!iframe) return;

      const player = new Vimeo(iframe, {
        background: true,
        muted: false,
        autoplay: false,
      });

      $map.data('player', player);

      if (this.isAudioMuted) {
        player.setMuted(this.isAudioMuted).then((muted) => {
          console.log('Muted?', muted);
        }).catch(console.error);
      }

      // Add cue points

      const cuePointData = $map.data('cuepoints');
      const cuePoints = [];
      let currentCuepoint = 0;

      cuePointData.forEach((point, index) => {
        const time = parseInt(point.seconds, 10);
        if (!time && time !== 0) return;
        player.addCuePoint(time, { index, title: point.name })
          .then((id) => {
            cuePoints.push({ id, data: { ...point, index } });
            $map.addClass('has-cue-points');
            console.log('Added cue point', id, point.time, point.name);
          }).catch(console.error);
      });

      // Hide overlay when playing

      player.on('play', () => {
        $('body').addClass('is-playing-video');
        $map.removeClass('is-initial');
        $map.addClass('is-playing');
        $map.removeClass('is-paused');
        trackEvent({ eventCategory: 'Video', eventAction: 'play', eventLabel: this.getSectionNameDefault($map) });
      });

      // Set paused state on video

      player.on('pause', () => {
        $('body').removeClass('is-playing-video');
        $map.removeClass('is-playing');
        $map.addClass('is-paused');
        trackEvent({ eventCategory: 'Video', eventAction: 'pause', eventLabel: this.getSectionNameDefault($map) });
      });

      player.on('ended', () => {
        trackEvent({ eventCategory: 'Video', eventAction: 'ended', eventLabel: this.getSectionNameDefault($map) });
      });

      // Play/pause on clicking the overlay trigger

      const $trigger = $map.find(selectors.videoMapTrigger);
      $trigger.on('click', () => {
        player.getPaused()
          .then(paused => (paused ? player.play() : player.pause()))
          .catch(() => {});
      });

      // Get available captions

      let captionLang = $('html').attr('lang') || 'de';
      let availableTrackLanguages;
      let activeTextTrack = null;

      player.getTextTracks().then((tracks) => {
        availableTrackLanguages = tracks.map(t => t.language);
        if (availableTrackLanguages.length) {
          $map.addClass('has-captions', true);
          if (availableTrackLanguages.includes(captionLang)) {
            $map.addClass('has-user-lang-caption', true);
          }
          else {
            [captionLang] = availableTrackLanguages;
          }
        }
      }).catch(() => {});

      // Toggle captions on button click

      player.on('texttrackchange', (data) => {
        if (data && data.language) {
          activeTextTrack = data.language;
          $map.addClass('is-showing-captions');
        }
        else {
          activeTextTrack = null;
          $map.removeClass('is-showing-captions');
        }
      });

      const $captionsToggle = $map.find(selectors.videoMapCaptionsToggle);

      $captionsToggle.on('click', () => {
        if (!activeTextTrack) {
          player.enableTextTrack(captionLang).then((track) => {
            console.log(`Enabling text track: ${track.label} (${track.language})`);
          }).catch(() => {
            console.error(`Invalid text track: ${captionLang}`);
          });
          trackEvent({ eventCategory: 'Video', eventAction: 'enable captions', eventLabel: this.getSectionNameDefault($map) });
        }
        else {
          player.disableTextTrack();
          trackEvent({ eventCategory: 'Video', eventAction: 'disable captions', eventLabel: this.getSectionNameDefault($map) });
        }
      });

      // Display captions

      const $captions = $map.find(selectors.videoMapCaptions);

      player.on('cuechange', (data) => {
        const html = data.cues.map(cue => cue.html).map(line => `<div>${line}</div>`);
        $captions.html(html);
      });

      // Cue point change (chapters)

      const $nav = $map.find(selectors.videoMapChapterTitle);
      const $prev = $map.find(selectors.videoMapChapterPrev);
      const $next = $map.find(selectors.videoMapChapterNext);
      const chapterFormat = $map.data('chapter-format') || '{current}/{total}';
      const formatNavTitle = (index, total) => chapterFormat
        .replace('{current}', index + 1)
        .replace('{total}', total);
      const setNavTitleToPoint = point => formatNavTitle(point.data.index, cuePoints.length);
      $nav.html(formatNavTitle(0, cuePointData.length));

      const updateCurrentCuepoint = (point) => {
        const index = cuePoints.findIndex(p => p.id === point.id);
        const prev = cuePoints[index - 1];
        const next = cuePoints[index + 1];
        $nav.html(setNavTitleToPoint(point));
        $prev.prop('disabled', !prev);
        $next.prop('disabled', !next);
        currentCuepoint = index;
        trackEvent({ eventCategory: 'Video', eventAction: 'enter', eventLabel: `${this.getSectionNameDefault($map)}: Kapitel ${index + 1}` });
      };

      player.on('cuepoint', (point) => {
        updateCurrentCuepoint(point);
      });

      const goToCuepoint = (index) => {
        const point = cuePoints[index];
        if (point) {
          $map.addClass('is-changing-cue-point');
          player.setCurrentTime(Math.max(0, point.data.seconds - 0.5))
            .then(() => {
              $map.removeClass('is-changing-cue-point');
              updateCurrentCuepoint(point);
            }).catch(console.error);
        }
      };

      $prev.on('click', () => {
        goToCuepoint(currentCuepoint - 1);
      });

      $next.on('click', () => {
        trackEvent({ eventCategory: 'Video', eventAction: 'skip', eventLabel: `${this.getSectionNameDefault($map)}: Kapitel ${currentCuepoint + 1}` });
        goToCuepoint(currentCuepoint + 1);
      });
    });

    $(document).on('beforecache', () => {
      const $map = $(selectors.videoMap);
      $map.removeClass('is-showing-captions');
    });
  }

  // Skip header

  setupSkipHeaderButton() {
    $(document).on('click', this.selectors.skipHeaderButton, (event) => {
      event.preventDefault();
      const $parent = $(event.target).scrollParent();
      const isBody = $parent.is(document) || $parent.is(window) || $parent.is('body');
      const element = isBody ? null : $parent.get(0);
      this.getScroller(element).toY($(window).height());
    });
  }

  // Open/close homepage info text

  setupHomepage() {
    $(document).on('click', this.selectors.homeTextToggle, (event) => {
      $('body').toggleClass('is-home-text-visible');
      event.preventDefault();
    });
  }

  // Video players / Plyr

  setupVideoPlayers() {
    const process = () => {
      const $videos = $(selectors.videoPlayer).once('video');
      $videos.each((i, node) => {
        const $video = $(node);

        // Find iframe and remove wrappers by assigning container the iframe html
        $video.html($video.find('iframe'));
        const $frame = $video.find('iframe');

        // Save current html to restore when caching page
        $video.data('html', $video.html());

        // Force src attr even when lazy-loaded
        $frame.attr('src', $frame.attr('src') || $frame.attr('data-src'));

        // Init Plyr player
        setTimeout(() => {
          // eslint-disable-next-line
          const player = new Plyr(node);
        }, 250);
      });
    };

    const cleanup = () => {
      // Restore video's original HTML
      $(selectors.videoPlayer).each((i, node) => {
        const $video = $(node);
        $video.html($video.data('html'));
      });
    };

    $(document).on('statechange', process);
    $(document).on('beforecache', cleanup);
  }


  // Audio players / Plyr

  setupAudioPlayers() {
    const process = () => {
      const $audios = $(selectors.audioPlayer).once('audio');
      $audios.each((i, node) => {
        const $audio = $(node);
        const title = $audio.data('title') || this.getSectionNameDefault($audio);
        $audio.data('parentHtml', $audio.parent().html());

        // Init Plyr player
        setTimeout(() => {
          // eslint-disable-next-line
          const player = new Plyr(node, {
            controls: ['play', 'duration', 'progress', 'current-time'],
            hideControls: false,
            tooltips: { controls: false, seek: false },
          });
          $(player.elements.container).addClass('plyr--initial');

          player.on('play', () => {
            $(player.elements.container).removeClass('plyr--initial');
            $(player.elements.container).addClass('plyr--used');

            trackEvent({ eventCategory: 'Audio', eventAction: 'play', eventLabel: title });
          });

          player.on('ended', () => {
            trackEvent({ eventCategory: 'Audio', eventAction: 'ended', eventLabel: title });
          });
        }, 250);
      });
    };

    const cleanup = () => {
      // Restore audio's original HTML
      $(selectors.audioPlayer).each((i, node) => {
        const $audio = $(node);
        $audio.parent().html($audio.data('parentHtml'));
      });
    };

    $(document).on('statechange', process);
    $(document).on('beforecache', cleanup);
  }

  // Slideshows / Swiper

  setupSlideshows() {
    $(document).on('statechange', () => {
      const $slideshows = $(selectors.slideshow).once('swiper');
      $slideshows.each((i, node) => {
        const $slideshow = $(node);
        // eslint-disable-next-line
        const mySwiper = new Swiper(node, {
          speed: 500,
          loop: true,
          grabCursor: true,
          autoHeight: true,
          navigation: {
            nextEl: '.js-slideshow-next',
            prevEl: '.js-slideshow-prev',
          },
          pagination: {
            el: '.js-slideshow-pagination',
            type: 'fraction',
            modifierClass: 'lorem-ipsum-',
          },
          on: {
            init() {
              $slideshow.addClass('is-setup');
              const numSlides = this.slides.length - 2; // account for loop
              if (numSlides <= 1) {
                this.allowTouchMove = false;
                this.unsetGrabCursor();
                $slideshow.addClass('is-single-slide');
              }
            },
          },
        });
      });
    });

    $(document).on('beforecache', () => {
      $(selectors.slideshow).each((i, node) => {
        if (node.swiper) {
          node.swiper.destroy();
        }
      });
    });
  }

  // Header slideshows / Swiper

  setupHeaderSlideshows() {
    $(document).on('statechange', () => {
      const $slideshow = $(selectors.headerSlideshow).once('swiper');
      if (!$slideshow.length) return;

      // eslint-disable-next-line
      const mySwiper = new Swiper($slideshow.get(0), {
        speed: 1000,
        loop: true,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        effect: 'fade',
        allowTouchMove: false,
        grabCursor: false,
        on: {
          init: () => {
            $slideshow.addClass('is-setup');
          },
        },
      });
    });

    $(document).on('beforecache', () => {
      $(selectors.headerSlideshow).each((i, node) => {
        if (node.swiper) {
          node.swiper.destroy();
        }
      });
    });
  }

  // Object slideshows / Swiper

  setupObjectSlideshows() {

    const calcNavHeight = (swiper) => {
      const slide = swiper.slides[swiper.activeIndex];
      if (slide) {
        const $image = $(slide).find('img');
        const $thread = $image.closest(selectors.thread);
        const imageHeight = $image.outerHeight();
        const navHeight = imageHeight ? `${imageHeight}px` : '100%';
        swiper.el.style.setProperty('--slideshow-nav-height', navHeight);
        $thread.get(0).style.setProperty('--slideshow-nav-height', navHeight);
      }
    };

    $(document).on('statechange', () => {
      const $slideshows = $(selectors.objectSlideshow).once('objectslideshow');

      if (!$slideshows.length) return;

      $slideshows.each((i, slideshow) => {
        const $slideshow = $(slideshow);

        // eslint-disable-next-line
        const mySwiper = new Swiper(slideshow, {
          speed: 400,
          grabCursor: false,
          slidesPerView: 1,
          autoHeight: true,
          breakpointsInverse: true,
          breakpoints: {
            400: {
              slidesPerView: 2,
            },
            580: {
              slidesPerView: 3,
            },
          },
          navigation: {
            nextEl: '.js-slideshow-next',
            prevEl: '.js-slideshow-prev',
          },
          on: {
            init() {
              const hasMultipleSlides = this.slides.length > 1;
              this.allowTouchMove = hasMultipleSlides;
              $slideshow.toggleClass('has-multiple-slides', hasMultipleSlides);
              $slideshow.addClass('is-setup');
              calcNavHeight(this);
            },
            slideChange() {
              calcNavHeight(this);
            },
            touchMove() {
              $slideshow.toggleClass('is-past-end', this.progress > 1.03);
              $slideshow.toggleClass('is-before-beginning', this.progress < -0.03);
            },
          },
        });
        $slideshows.addClass('is-setup');
      });
    });

    $(document).on('beforecache', () => {
      $(selectors.objectSlideshow).each((i, node) => {
        if (node.swiper) {
          node.swiper.destroy();
        }
      });
    });
  }
}
